import { useEffect, useMemo, useRef, useState } from "react"
import type { PermanentState } from "../types"
import { getDialogueById } from "../game/content"
import { getSpeakerStyle } from "../game/sprites"

interface EventScreenProps {
  dialogueId: string
  permanent: PermanentState
  /** Pass over this conversation at once if it has already been read to its end. */
  autoSkipSeen?: boolean
  onContinue: (grantedKnowledge: string[]) => void
}

export function EventScreen({
  dialogueId,
  permanent,
  autoSkipSeen = false,
  onContinue,
}: EventScreenProps) {
  const dialogue = getDialogueById(dialogueId)
  const [lineIndex, setLineIndex] = useState(0)
  // Focus target for the primary button, so a dialogue that opens is
  // immediately keyboard-actionable without a stray Tab press first.
  const advanceRef = useRef<HTMLButtonElement | null>(null)

  const lines = dialogue?.lines ?? []
  const seenMarker = `seen_${dialogueId}`
  const alreadySeen = permanent.knowledge.includes(seenMarker)
  // Constant for a given dialogue (the screen remounts per dialogueId), so it
  // stays referentially stable and doesn't re-bind the key handler each render.
  const grantedKnowledge = useMemo(
    () => [
      ...(dialogue?.lines ?? []).flatMap((line) => line.grantsKnowledge ?? []),
      seenMarker,
    ],
    [dialogue, seenMarker],
  )

  const isLast = lineIndex >= lines.length - 1
  const canGoBack = lineIndex > 0

  // When the player has asked to skip conversations they have already read, a
  // previously-seen dialogue is passed over the moment it opens — but its
  // knowledge is still granted, so skipping never costs the player anything the
  // conversation would have taught. The guard makes it fire exactly once even
  // under React's development double-invocation.
  const autoSkipped = useRef(false)
  useEffect(() => {
    if (!dialogue || !autoSkipSeen || !alreadySeen) return
    if (autoSkipped.current) return
    autoSkipped.current = true
    onContinue(grantedKnowledge)
  }, [dialogue, autoSkipSeen, alreadySeen, grantedKnowledge, onContinue])

  function handleNext() {
    if (isLast) {
      onContinue(grantedKnowledge)
    } else {
      setLineIndex((i) => i + 1)
    }
  }

  function handleBack() {
    setLineIndex((i) => Math.max(0, i - 1))
  }

  // Dialogue is the game's most frequent interaction, and until now every line
  // of every conversation had to be clicked through with the mouse. Space,
  // Enter and → advance it; ← and Backspace step back to re-read a line the
  // player skimmed past. A button that already has focus keeps its native
  // activation, so a keypress never advances twice.
  useEffect(() => {
    if (!dialogue) return
    const advance = () => {
      if (lineIndex >= lines.length - 1) onContinue(grantedKnowledge)
      else setLineIndex(lineIndex + 1)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (event.key === "ArrowLeft" || event.key === "Backspace") {
        if (lineIndex === 0) return
        event.preventDefault()
        setLineIndex(lineIndex - 1)
        return
      }
      // → is not a native button activator, so it advances regardless of what
      // has focus. Enter and Space are: if the advance button already holds
      // focus, its own activation handles them, so the handler stands aside to
      // avoid paging twice on a single press.
      if (event.key === "ArrowRight") {
        event.preventDefault()
        advance()
        return
      }
      if (event.key === "Enter" || event.key === " ") {
        if (document.activeElement instanceof HTMLButtonElement) return
        event.preventDefault()
        advance()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
    // Re-bound each line so the closure sees the current index.
  }, [dialogue, lineIndex, lines.length, grantedKnowledge, onContinue])

  // Keep the primary action focused as the conversation advances, so the
  // keyboard flow never silently strands focus on a button that just vanished
  // (e.g. Back disappearing on the way to the first line).
  useEffect(() => {
    advanceRef.current?.focus()
  }, [lineIndex])

  if (!dialogue) {
    return (
      <div className="screen event-screen">
        <p>...</p>
        <button type="button" onClick={() => onContinue([])}>
          Continue
        </button>
      </div>
    )
  }

  const currentLine = lines[lineIndex]
  const speakerStyle = getSpeakerStyle(dialogue.speaker)

  return (
    <div className="screen event-screen">
      <div className="event-speaker">
        {speakerStyle && (
          <span className="speaker-portrait" style={speakerStyle} role="img" aria-hidden="true" />
        )}
        <h1>{dialogue.speaker}</h1>
      </div>
      {/* Keyed by line so each new line of prose is penned on afresh (a wet-ink
          bleed) rather than snapping in place as the text swaps. */}
      <p key={lineIndex} className="event-line">
        {currentLine.text}
      </p>
      <div className="event-actions">
        <button ref={advanceRef} type="button" onClick={handleNext}>
          {isLast ? "Continue" : "Next"}
        </button>
        {canGoBack && (
          <button type="button" onClick={handleBack}>
            Back
          </button>
        )}
        {alreadySeen && !isLast && (
          <button type="button" onClick={() => onContinue(grantedKnowledge)}>
            Skip
          </button>
        )}
      </div>
      {/* How much of this conversation is left, and how to move through it
          without reaching for the mouse. Hidden for single-line beats, where
          there is nothing to page through. */}
      {lines.length > 1 && (
        <p className="event-progress">
          <span className="event-progress-count">
            {lineIndex + 1} / {lines.length}
          </span>
          <span className="event-progress-hint" aria-hidden="true">
            Space or → to continue{canGoBack ? " · ← to go back" : ""}
          </span>
        </p>
      )}
    </div>
  )
}
